/*
 * Copyright (c) 2020  Apricad LLC. All Rights Reserved.
 */

import * as React from "react";
import { useState } from "react";
import { connect } from "react-redux";
import { Redirect } from "react-router-dom";
import { Formik, Form } from "formik";

// @material-ui/core
import createStyles from "@material-ui/core/styles/createStyles";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";
import { Grid, Hidden, Fab } from "@material-ui/core";

// @material-ui/icons
import { YouTube, CardGiftcard } from "@material-ui/icons";

// CT Kit
import Parallax from "components/Parallax/Parallax.jsx";
import InfoArea from "components/InfoArea/InfoArea.jsx";
import Button from "components/CustomButtons/Button.jsx";

import { Modal } from "components/Modal/Modal";

import { updateUser, ApricotAction } from "client/actions/user";
import { welcomeValidation, btnFormSubmit } from "components/Form/FormField";
import { renderJobSection, renderHowFoundSection } from "./accountUtils";
import {
  sWelcome,
  sLegal,
  sBack,
  sTerms,
  sPolicy,
  sByStart,
  sChannel,
  sUpdateProfile,
} from "assets/jss/views/ResStrings";
import TermOfUse from "client/views/Legal/Content/Terms";
import Policy from "client/views/Legal/Content/Policy";

import accountPageStyle from "assets/jss/views/accountPage/accountPageStyle.jsx";
const styles = createStyles(accountPageStyle as any);

interface FormValues {
  jobs: string[];
  howf: string[];
}

interface Props extends WithStyles<typeof styles> {
  user: any;
  updateUser: ApricotAction;
}

const WelcomePageBase: React.FC<Props> = ({
  user,
  updateUser,
  classes,
}: Props) => {
  const [showTerms, setShowTerms] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);

  const renderGreeting = () => {
    return (
      <>
        <InfoArea
          className={classes.infoArea}
          title="Thanks for signing up!"
          description=""
          icon={CardGiftcard}
          iconColor="success"
        />
        <div>
          <p>{sWelcome}</p>
          {buttonYT(true)}
        </div>
      </>
    );
  };

  const registerUser = (values: FormValues) => {
    updateUser(user._id, {
      jobs: values.jobs,
      howf: values.howf,
      registered: true,
    });
  };

  const renderAgreement = () => {
    return (
      <p>
        {sByStart}
        &nbsp;
        <span
          className={classes.legalNote}
          style={{ fontSize: "1.2rem" }}
          onClick={() => setShowTerms(true)}>
          {sTerms}
        </span>
        &nbsp;&amp;&nbsp;
        <span
          className={classes.legalNote}
          style={{ fontSize: "1.2rem" }}
          onClick={() => setShowPolicy(true)}>
          {sPolicy}
        </span>
      </p>
    );
  };

  if (!user) {
    return <Redirect to="/signin" />;
  }

  // if user has completed registration, they should not be here
  if (user.registered) {
    return <Redirect to="/cr_list" />;
  }

  return (
    <>
      <Parallax image={require("assets/img/welcome.jpg")} filter="dark" banner>
        <div className={classes.container}>
          <div className={classes.brand}>
            <h1 className={classes.title}>Welcome on Board</h1>
          </div>
        </div>
      </Parallax>

      <Formik
        initialValues={{
          // giftChoice: "",
          jobs: [],
          howf: [],
        }}
        onSubmit={(values: FormValues) => {
          registerUser(values); // complete registration
        }}
        validationSchema={welcomeValidation}
        render={(props) => (
          <Form>
            <div className={classes.pageContent} style={{ padding: "30px 0" }}>
              <div className={classes.container}>
                <Grid container justify="center">
                  <Grid item xs={12} sm={6} md={6}>
                    <div className={classes.profileGrid}>
                      {renderGreeting()}
                      {renderJobSection(props, classes)}
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <div className={classes.profileGrid}>
                      {renderHowFoundSection(props, classes)}
                    </div>
                  </Grid>
                  <Grid />
                </Grid>
                <div className={classes.reminder}>
                  <p>{sUpdateProfile}</p>
                  {user.localId === undefined && renderAgreement()}
                  <span>
                    {btnFormSubmit({
                      label: "Let's Get started!",
                      disabled: !props.isValid,
                      size: "lg",
                    })}
                  </span>
                </div>
              </div>
            </div>
          </Form>
        )}
      />
      {showTerms && (
        <Modal
          wide
          title={sLegal}
          buttons={[sBack]}
          onExit={() => setShowTerms(false)}>
          <TermOfUse
            onShowPolicy={() => {
              setShowPolicy(true);
            }}
          />
        </Modal>
      )}
      {showPolicy && (
        <Modal
          wide
          title={sLegal}
          buttons={[sBack]}
          onExit={() => setShowPolicy(false)}>
          <Policy />
        </Modal>
      )}
    </>
  );
};

function mapStateToProps(state: any) {
  return {
    user: state.state_auth,
  };
}

const base: any = WelcomePageBase;
export const WelcomePage: any = {
  component: withStyles(styles)(connect(mapStateToProps, { updateUser })(base)),
};

export const WelcomePageTest = withStyles(styles)(base);

export const buttonYT = (
  alwaysLarge: boolean,
  link?: string,
  label?: string,
) => {
  const subscribe = () =>
    window.open(link || `${sChannel}?sub_confirmation=1`, "_blank");

  const btn = (
    <Button color="youtube" round size="lg" onClick={subscribe}>
      <i
        className="fab fa-youtube"
        style={{
          width: "56px",
          height: "30px",
          margin: "-0.5rem 0 -0.5rem -0.5rem",
        }}></i>
      {label || "SUBSCRIBE"}
    </Button>
  );

  return alwaysLarge ? (
    btn
  ) : (
    <>
      <Hidden xsDown>{btn}</Hidden>
      <Hidden smUp>
        <Fab
          style={{ backgroundColor: "red", color: "white" }}
          color="inherit"
          onClick={subscribe}>
          <YouTube fontSize="large" />
        </Fab>
      </Hidden>
    </>
  );
};

// const renderGift = (props: any) => {
//   const { values, errors, touched } = props;

//   return (
//     <>
//       <InfoArea
//         className={classes.infoArea}
//         title="Pick up the Free Course!"
//         description=""
//         icon={Gift}
//         iconColor="warning"
//       />
//       <RadioButtonGroup
//         id="giftChoice"
//         value={values.giftChoice}
//         error={errors.giftChoice}
//         touched={touched.giftChoice}
//         classes={classes}>
//         <Field
//           component={FormRadioButtonField}
//           name="giftChoice"
//           id="g1"
//           label="Nursing Care Plan"
//         />
//         <Field
//           component={FormRadioButtonField}
//           name="giftChoice"
//           id="g2"
//           label="Nursing Diagnosis 101"
//         />
//       </RadioButtonGroup>
//     </>
//   );
// };

// // Radio group
// const RadioButtonGroup = (props: any) => {
//   const { handleChange, error, touched, classes, children } = props;
//   return (
//     <div style={{ maxWidth: "370px", margin: "0.2rem auto" }}>
//       <FormControl component="fieldset">
//         <FormLabel component="legend">
//           Choose one from these two courses as our welcome gift.
//         </FormLabel>
//         <RadioGroup
//           aria-label="Choose a free course:"
//           name="giftChoice"
//           onChange={handleChange}
//           children={children}
//         />
//       </FormControl>
//       <br />
//       {error && touched && (
//         <span className={classes.inputError}>&bull;&nbsp;{error}</span>
//       )}
//     </div>
//   );
// };
